import axios from "axios";
import { getBaseUrl } from "./api";
import type { CheckoutApiConfig } from "./types";

/**
 * Equipment (resource-pool) availability for the checkout pickers.
 *
 * ADVISORY: the server's reserve/charge paths stay authoritative. The
 * numbers here come from the same ledger math that refuses at checkout, so a
 * picker capped by them can only lose a race — never offer something checkout
 * categorically rejects.
 *
 * FAIL OPEN is the load-bearing rule: any fetch failure, missing endpoint
 * (widget/youmo deploy independently of the API), or a session absent from
 * the response reads as UNCAPPED — never as 0. A pool constraint that fails
 * to load must degrade to today's behaviour, not to "sold out".
 */

export type PoolAvailabilityWindow = {
  key: string;
  start: string;
  end: string;
  freeUnits: number;
  maxTickets: number;
};

export type PoolAvailabilityResponse = {
  poolId?: string;
  totalUnits?: number;
  unitsPerTicket?: number;
  windows?: PoolAvailabilityWindow[];
  termAggregates?: Record<string, { freeUnits: number; maxTickets: number }>;
};

/** maxTickets per window key (sessionId / slot ISO), absent = uncapped. */
export type PoolCapsByKey = Record<string, number>;

/** The orchestrator's aggregated caps, per pool-bound ticket type. */
export type CheckoutPoolAvailability = {
  /** ticketTypeId → (sessionId → maxTickets). */
  capsByTicket: Record<string, PoolCapsByKey>;
  /** ticketTypeId → (termEventId → min across the term's occurrences). */
  termAggregatesByTicket: Record<
    string,
    Record<string, { freeUnits: number; maxTickets: number }>
  >;
};

/**
 * The per-ticket cap map for ONE selected window set: single session → its
 * key; multi-date → the MIN across the selected keys (the all-or-nothing
 * charge gate refuses the whole selection if any date is short); term → the
 * termAggregates key. Absent entries stay absent (fail open).
 */
export function poolCapsForKeys(
  availability: CheckoutPoolAvailability | null | undefined,
  keys: string[],
  opts?: { termEventId?: string },
): Record<string, number> | undefined {
  if (!availability) return undefined;
  const out: Record<string, number> = {};
  if (opts?.termEventId) {
    for (const [ticketId, aggs] of Object.entries(
      availability.termAggregatesByTicket,
    )) {
      const agg = aggs[opts.termEventId];
      if (agg) out[ticketId] = agg.maxTickets;
    }
  } else {
    for (const [ticketId, caps] of Object.entries(availability.capsByTicket)) {
      let min: number | null = null;
      for (const key of keys) {
        const cap = caps[key];
        // Window outside the fetched range — no information, so it contributes
        // no constraint. It is still gated at checkout.
        if (cap == null) continue;
        min = min == null ? cap : Math.min(min, cap);
      }
      // Cap on PARTIAL knowledge: the tightest known window wins, even when
      // some selected windows are unknown. Only a selection where nothing at
      // all is known stays uncapped.
      //
      // (This used to read `min != null && (!missing || keys.length > 0)`.
      // The second half was dead — reaching `min != null` means a key was
      // iterated, so `keys.length > 0` was always true — and had it ever
      // fired it would have been the WRONG behaviour: dropping the cap
      // because one window is unknown offers more than the known windows
      // allow, which is the picker-offers-what-checkout-refuses shape this
      // whole cap exists to prevent.)
      if (min != null) out[ticketId] = min;
    }
  }
  return Object.keys(out).length ? out : undefined;
}

export async function fetchPoolAvailability(
  courseId: string,
  ticketTypeId: string,
  config: CheckoutApiConfig,
  options: {
    from?: string;
    to?: string;
    date?: string;
    durationMinutes?: number;
    /**
     * Rental mode: price EXACTLY these start instants instead of a grid
     * stepped from `date`. The picker's start options are a union of one
     * sequence per opening window, which no single anchor can enumerate.
     */
    starts?: string[];
    excludeReservationIds?: string[];
  },
): Promise<PoolAvailabilityResponse | null> {
  const baseUrl = getBaseUrl(config);
  try {
    const response = await axios.get<PoolAvailabilityResponse>(
      `${baseUrl}/widget/courses/${courseId}/pool-availability`,
      {
        params: {
          ticketTypeId,
          from: options.from,
          to: options.to,
          date: options.date,
          durationMinutes: options.durationMinutes,
          starts: options.starts?.length ? options.starts.join(",") : undefined,
          excludeReservationIds: options.excludeReservationIds?.length
            ? options.excludeReservationIds.join(",")
            : undefined,
        },
      },
    );
    const data = response.data;
    if (!data || !Array.isArray(data.windows)) return null; // {} = no pool = uncapped
    return data;
  } catch {
    // FAIL OPEN — see module doc.
    return null;
  }
}

/** Flatten a response into a key → maxTickets map (null response = no caps). */
export function poolCapsByKey(
  response: PoolAvailabilityResponse | null,
): PoolCapsByKey | null {
  if (!response || !Array.isArray(response.windows)) return null;
  const caps: PoolCapsByKey = {};
  for (const w of response.windows) caps[w.key] = w.maxTickets;
  return caps;
}

/**
 * The one cap formula: what the seat picker may offer for a pool-bound
 * ticket. `poolMaxTickets` undefined/null = uncapped (no pool, failed fetch,
 * or window missing from the response — all fail open). Applied
 * UNCONDITIONALLY, independent of guestsPerTicket/affectsAvailability: a
 * zero-guest equipment add-on ticket bypasses every session-level cap and is
 * exactly the ticket a pool must still bound.
 */
/**
 * One pool's cap, SHARED across every ticket type drawing on it.
 *
 * The cap arrives per ticket type, but a pool is one physical stock and
 * resolvePoolTicketsForBooking sums units per pool — so applying each ticket's
 * cap independently offers a 2-unit pool as "max 2" on Adult AND "max 2" on
 * Child, and 2+2 is then refused at payment. That is the offer-then-refuse the
 * cap exists to prevent.
 *
 * Worked in UNITS because siblings need not cost the same: maxTickets is
 * floor(freeUnits / unitsPerTicket), so `poolMaxTickets * myUnits` recovers a
 * lower bound on the free units, and the remainder lost to that floor only
 * ever makes the answer stricter.
 *
 * Pure and exported so it can be tested without rendering anything — the
 * previous version lived inline in the component, where nothing executed it.
 */
export function poolCapAcrossTicketTypes(params: {
  ticketTypeId: string;
  resourcePoolId?: string | null;
  resourceUnitsPerTicket?: number | null;
  poolMaxTickets: number | null | undefined;
  /** Every ticket type on the listing, for finding pool siblings. */
  ticketTypes: Array<{
    id: string;
    resourcePoolId?: string | null;
    resourceUnitsPerTicket?: number | null;
  }>;
  selections: Array<{ ticketTypeId: string; quantity: number }>;
}): number | null | undefined {
  const {
    ticketTypeId,
    resourcePoolId,
    poolMaxTickets,
    ticketTypes,
    selections,
  } = params;
  if (poolMaxTickets == null || !resourcePoolId) return poolMaxTickets;

  const myUnits = Math.max(1, params.resourceUnitsPerTicket ?? 1);
  const siblingUnits = selections.reduce((total, sel) => {
    if (sel.ticketTypeId === ticketTypeId) return total;
    const sibling = ticketTypes.find(t => t.id === sel.ticketTypeId);
    if (!sibling || sibling.resourcePoolId !== resourcePoolId) return total;
    return total + sel.quantity * Math.max(1, sibling.resourceUnitsPerTicket ?? 1);
  }, 0);

  return Math.max(0, Math.floor((poolMaxTickets * myUnits - siblingUnits) / myUnits));
}

export function capSeatsByPool(
  sessionAvailability: number,
  poolMaxTickets: number | null | undefined,
): number {
  if (poolMaxTickets == null || !Number.isFinite(poolMaxTickets)) {
    return sessionAvailability;
  }
  return Math.min(sessionAvailability, Math.max(0, poolMaxTickets));
}


/**
 * Which ticket type the slot fetch should ask the pool cap for.
 *
 * The server only computes a slot's pool cap when the request names a ticket
 * type. Variable-duration appointments pick the ticket BEFORE the slot, so the
 * selection is the answer. Fixed-duration ones pick the slot first, so there is
 * no selection yet at fetch time — which is why the cap was never computed for
 * them at all, the common appointment shape.
 *
 * An appointment booking is 1-to-1 with a ticket type, so when the schedule
 * offers exactly one the answer is unambiguous. With several we send nothing
 * rather than guess: a cap computed against the wrong pool is a confidently
 * wrong number, which is worse than no number. Checkout still refuses either
 * way — this only decides what the customer is shown beforehand.
 */
/**
 * Whether a course lets the customer choose their own duration.
 *
 * A one-line read, extracted only because getting it wrong is invisible:
 * `durationMode` lives under `appointmentScheduleSettings`, and reading it off
 * the top level yields `undefined`, which silently means "fixed" — so the
 * caller keeps working while always taking the wrong branch. That exact
 * mistake shipped and survived a review. As a pure function it can at least be
 * pinned by a test.
 */
export function isVariableDurationCourse(course: any): boolean {
  return course?.appointmentScheduleSettings?.durationMode === "variable";
}

export function appointmentCapTicketTypeId(params: {
  isVariableDuration: boolean;
  selectedTicketTypeId?: string | null;
  ticketTypeIds: string[];
}): string | undefined {
  const { isVariableDuration, selectedTicketTypeId, ticketTypeIds } = params;
  if (isVariableDuration) return selectedTicketTypeId ?? undefined;
  return ticketTypeIds.length === 1 ? ticketTypeIds[0] : undefined;
}

/**
 * The cap map for an appointment's ticket selector, which already knows how to
 * render `{ ticketTypeId: max }`.
 *
 * One entry, because an appointment booking draws on one ticket type. An
 * ABSENT cap means "no pool, or the server was never asked" and must stay
 * uncapped — collapsing it to 0 would make every unlimited appointment
 * unbookable. A cap of 0 is real and is preserved.
 */
export function appointmentPoolCaps(
  ticketTypeId: string | null | undefined,
  poolMaxTickets: number | null | undefined,
): Record<string, number> | undefined {
  if (!ticketTypeId) return undefined;
  if (typeof poolMaxTickets !== "number" || !Number.isFinite(poolMaxTickets)) {
    return undefined;
  }
  return { [ticketTypeId]: Math.max(0, poolMaxTickets) };
}

/**
 * Whether a slot's remaining equipment covers the quantity the customer has
 * ALREADY chosen, and how many are left.
 *
 * Variable-duration appointments pick the ticket and its quantity BEFORE the
 * slot exists (`visibleSteps` is ["tickets", "timeslot"]), so the quantity
 * stepper cannot be capped by a slot — the slot grid is the only surface left.
 * This is the same fail-open contract as `capSeatsByPool`: an absent or
 * non-finite cap is "no information", which reads as UNCAPPED and never as
 * sold out.
 *
 * `requestedQuantity <= 0` means the customer has not asked for anything yet
 * (the fixed-duration order, where the slot comes first), so it constrains
 * nothing. A cap of 0 is real and is preserved — though the server already
 * drops those slots from the grid, so it should not arrive here.
 */
export function slotPoolFit(params: {
  poolMaxTickets: number | null | undefined;
  /**
   * Exact free units on this slot's pool. Preferred over `poolMaxTickets`
   * whenever the API sends it, because maxTickets is
   * floor(freeUnits / unitsPerTicket) and that floor discards a remainder the
   * client cannot recover. On a 3-unit pool measured through a 2-unit ticket
   * it reports 1, so a genuinely bookable 2-unit + 1-unit pair is refused —
   * a combination the customer can then NEVER book, not a lost race.
   *
   * Optional because the widget and the API deploy independently: an older API
   * sends nothing here and must fall back to the ticket comparison, never to
   * "sold out".
   */
  poolFreeUnits?: number | null;
  requestedQuantity: number;
  /** Demand in POOL UNITS across every same-pool selection, when known. */
  requestedUnits?: number | null;
}): { fits: boolean; remaining: number | null } {
  const { poolMaxTickets, poolFreeUnits, requestedQuantity, requestedUnits } =
    params;

  const remaining =
    typeof poolMaxTickets === "number" && Number.isFinite(poolMaxTickets)
      ? Math.max(0, poolMaxTickets)
      : null;

  // Exact path: both halves known, so compare the real numbers.
  if (
    typeof poolFreeUnits === "number" &&
    Number.isFinite(poolFreeUnits) &&
    typeof requestedUnits === "number" &&
    Number.isFinite(requestedUnits)
  ) {
    if (requestedUnits <= 0) return { fits: true, remaining };
    return { fits: Math.max(0, poolFreeUnits) >= requestedUnits, remaining };
  }

  if (remaining === null) return { fits: true, remaining: null };
  if (!Number.isFinite(requestedQuantity) || requestedQuantity <= 0) {
    return { fits: true, remaining };
  }
  return { fits: remaining >= requestedQuantity, remaining };
}

/**
 * Total demand on the measured ticket's pool, expressed in TICKETS OF THAT
 * TICKET so it can be compared against the slot's `poolMaxTickets` directly.
 *
 * The slot grid carries ONE number per slot — the cap for the ticket the
 * server was asked about — but that number bounds the whole shared pool, not
 * that one ticket. Measuring only the asked ticket's own quantity therefore
 * lets siblings on the same pool through: 2 Adult + 2 Child against 3 units
 * reports 2, passes `3 >= 2`, and is refused at charge time. That is the
 * offer-then-refuse the cap exists to prevent, and it is the same reasoning
 * `poolCapAcrossTicketTypes` applies on the quantity stepper.
 *
 * Worked in units and converted back with ceil: `maxTickets` is
 * floor(freeUnits / unitsPerTicket), so `maxTickets * measuredUnits` is a
 * LOWER bound on the free units. Rounding the demand up keeps the comparison
 * on the safe side of that bound.
 *
 * ⚠ That rounding is not merely conservative when the measured ticket costs
 * more than one unit: on a 3-unit pool measured through a 2-unit ticket,
 * `maxTickets` is 1, and a genuinely bookable 2-unit + 1-unit pair rounds to 2
 * and is refused — a combination the customer can then never book at all. This
 * is why `poolDemandUnitsOnPool` and the slot's `poolFreeUnits` exist; prefer
 * them. This function is the FALLBACK for an API that predates that field
 * (the widget and the API deploy independently), where the rounded comparison
 * is still better than no cap. It is exact whenever the measured ticket costs
 * one unit, which is the ordinary case.
 *
 * A measured ticket with no pool contributes only its own quantity: nothing
 * can be known to share a pool that does not exist, and `poolMaxTickets` will
 * be absent anyway, so the number cannot affect the outcome.
 */
export function poolDemandInTicketsOf(params: {
  measuredTicketTypeId: string | null | undefined;
  ticketTypes: Array<{
    id: string;
    resourcePoolId?: string | null;
    resourceUnitsPerTicket?: number | null;
  }>;
  selections: Array<{ ticketTypeId: string; quantity: number }>;
}): number {
  const { measuredTicketTypeId, ticketTypes, selections } = params;
  if (!measuredTicketTypeId) return 0;

  const ownQuantity = () =>
    selections
      .filter(sel => sel.ticketTypeId === measuredTicketTypeId)
      .reduce((sum, sel) => sum + sel.quantity, 0);

  const measured = ticketTypes.find(t => t.id === measuredTicketTypeId);
  if (!measured?.resourcePoolId) return ownQuantity();

  const measuredUnits = Math.max(1, measured.resourceUnitsPerTicket ?? 1);
  const demandUnits = poolDemandUnitsOnPool(params) ?? 0;

  return Math.ceil(demandUnits / measuredUnits);
}

/**
 * The same demand, left in POOL UNITS.
 *
 * This is the number to compare against a slot's `poolFreeUnits`, and it is
 * exact — no floor, no ceil. `poolDemandInTicketsOf` exists only for the
 * fallback path where the API sends `poolMaxTickets` alone.
 *
 * null when nothing is measured, or the measured ticket carries no pool: there
 * is no pool to spend units from, so there is no number to compare.
 */
export function poolDemandUnitsOnPool(params: {
  measuredTicketTypeId: string | null | undefined;
  ticketTypes: Array<{
    id: string;
    resourcePoolId?: string | null;
    resourceUnitsPerTicket?: number | null;
  }>;
  selections: Array<{ ticketTypeId: string; quantity: number }>;
}): number | null {
  const { measuredTicketTypeId, ticketTypes, selections } = params;
  if (!measuredTicketTypeId) return null;
  const measured = ticketTypes.find(t => t.id === measuredTicketTypeId);
  if (!measured?.resourcePoolId) return null;

  return selections.reduce((total, sel) => {
    const ticket = ticketTypes.find(t => t.id === sel.ticketTypeId);
    if (!ticket || ticket.resourcePoolId !== measured.resourcePoolId) return total;
    return total + sel.quantity * Math.max(1, ticket.resourceUnitsPerTicket ?? 1);
  }, 0);
}

/**
 * The cap map for an appointment slot that was measured for SEVERAL tickets.
 *
 * The sibling `appointmentPoolCaps` builds a one-entry map from the scalar the
 * single-ticket path produces; this reads the per-ticket map the multi-ticket
 * path stamps on the slot. Same contract: an absent or unusable entry is
 * omitted (uncapped), a real 0 is preserved, and an empty result is undefined
 * so the selector is not handed a map that constrains nothing.
 */
export function appointmentPoolCapsFromSlot(
  byTicketTypeId: Record<string, unknown> | null | undefined,
): Record<string, number> | undefined {
  if (!byTicketTypeId || typeof byTicketTypeId !== "object") return undefined;
  const out: Record<string, number> = {};
  for (const [ticketTypeId, value] of Object.entries(byTicketTypeId)) {
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    out[ticketTypeId] = Math.max(0, value);
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * Whether the current selections overdraw any pool they draw on.
 *
 * PER POOL, not per ticket. Comparing each selection against its own cap
 * misses the case the multi-ticket map exists for: two tickets on one pool can
 * each sit inside their own number while jointly exceeding the stock. Two free
 * units reported as `{adult: 2, child: 2}` means "either of you may take 2",
 * not "both of you may".
 *
 * The bound is recovered in units. `cap` is floor(freeUnits / unitsPerTicket)
 * for that ticket, so `cap * units` is a lower bound on the pool's free units;
 * the largest such bound across the pool's capped tickets is the best estimate
 * available, and the floor's remainder only ever makes it stricter.
 *
 * A pool no ticket carries a cap for contributes nothing — no information,
 * which reads as uncapped, never as full.
 *
 * This backstops a selection that outlived the slot it was made against. What
 * the customer can SET is already bounded by poolCapAcrossTicketTypes in the
 * selector; a selection carried to a tighter slot is not.
 */
export function poolSelectionsExceedCaps(params: {
  ticketTypes: Array<{
    id: string;
    resourcePoolId?: string | null;
    resourceUnitsPerTicket?: number | null;
  }>;
  selections: Array<{ ticketTypeId: string; quantity: number }>;
  capsByTicketTypeId: Record<string, number> | undefined;
}): boolean {
  const { ticketTypes, selections, capsByTicketTypeId } = params;
  if (!capsByTicketTypeId) return false;

  const demandByPool = new Map<string, number>();
  const boundByPool = new Map<string, number>();

  for (const ticket of ticketTypes) {
    const poolId = ticket.resourcePoolId;
    if (!poolId) continue;
    const units = Math.max(1, ticket.resourceUnitsPerTicket ?? 1);

    const quantity = selections
      .filter(sel => sel.ticketTypeId === ticket.id)
      .reduce((sum, sel) => sum + sel.quantity, 0);
    if (quantity > 0) {
      // Counted whether or not THIS ticket carries a cap. A pool selection is
      // conjunctive — every selected ticket eats the same stock at the same
      // moment — so an uncapped sibling's units are real consumption, not
      // missing information. (An earlier version skipped the whole pool when
      // any drawing ticket lacked a cap, by analogy with rentalDayPoolCap.
      // That analogy is wrong: rental starts are DISJUNCTIVE, the customer
      // takes one, so an unknown start is an alternative. See poolCapsForKeys
      // above, which caps ON partial knowledge for the same conjunctive
      // reason.)
      demandByPool.set(poolId, (demandByPool.get(poolId) ?? 0) + quantity * units);
    }

    const cap = capsByTicketTypeId[ticket.id];
    if (typeof cap === "number" && Number.isFinite(cap)) {
      const bound = Math.max(0, cap) * units;
      const prev = boundByPool.get(poolId);
      boundByPool.set(poolId, prev == null ? bound : Math.max(prev, bound));
    }
  }

  for (const [poolId, demand] of demandByPool) {
    const bound = boundByPool.get(poolId);
    if (bound == null) continue; // nothing known about this pool at all
    if (demand > bound) return true;
  }
  return false;
}


/**
 * Whether the APPOINTMENT FLOW runs in variable-duration mode.
 *
 * `isVariableDurationCourse` answers a narrower question — what the course is
 * configured as — and answering the flow's question with it has now drifted
 * three times. The flow requires all three clauses: variable duration, NOT a
 * group-schedule booking, and v2 tickets. Miss the second and a group booking
 * of a variable course is treated as variable while the flow treats it as
 * fixed; anything deciding whether to fetch equipment caps on that basis
 * withholds them from a surface that consumes them, which is the oversell
 * direction.
 *
 * Keep this the single source for THE FLOW's question. `checkout-appointment`
 * and the orchestrator both call it, and the group-schedule clause is pinned
 * by a test because it is the one that kept getting dropped.
 *
 * ONE site deliberately does not use it: `checkout-calendar-visibility` feeds
 * bare `isVariableDurationCourse` into `appointmentCapTicketTypeId`. That
 * surface has no variable mode — it always runs fixed — and the bare course
 * check is protective there: naming a ticket id would flip the SERVER into
 * ticket-filtered slot generation for a variable-duration course and can empty
 * the grid. The cost is that a variable-duration listing renders uncapped on
 * that surface. Do not "unify" it without reading the comment at its call
 * site first.
 */
export function isVariableDurationFlow(params: {
  course: any;
  isGroupSchedule?: boolean;
}): boolean {
  const { course, isGroupSchedule } = params;
  return (
    isVariableDurationCourse(course) &&
    !isGroupSchedule &&
    course?.ticketVersion === "v2"
  );
}

/**
 * Whether this ticket cannot be booked because its POOL has no room left.
 *
 * Two ways that happens, and the second is why an explicit `cap === 0` check
 * is not enough. A pooled ticket can be ABSENT from the cap map — beyond the
 * server's eight-id truncation, it may have had no window for this slot, or
 * its lookup may have failed and been swallowed so the rest still answered.
 * The selector then renders it uncapped and selectable, while
 * `poolSelectionsExceedCaps` refuses any quantity of it against a capped
 * sibling's zero bound. The customer is told the slot lacks equipment with no
 * row showing a limit, and told to select a ticket if they drop to zero: a
 * dead end with two wrong messages.
 *
 * A ticket with no pool is never unbookable for this reason — it is exactly
 * the ticket the annotate-only rule keeps the slot on the grid for.
 */
export function ticketIsUnbookableOnPool(params: {
  ticket: {
    id: string;
    resourcePoolId?: string | null;
    resourceUnitsPerTicket?: number | null;
  };
  ticketTypes: Array<{
    id: string;
    resourcePoolId?: string | null;
    resourceUnitsPerTicket?: number | null;
  }>;
  capsByTicketTypeId: Record<string, number> | undefined;
}): boolean {
  const { ticket, ticketTypes, capsByTicketTypeId } = params;
  if (!capsByTicketTypeId || !ticket.resourcePoolId) return false;

  // A sibling costing U units and reporting cap 0 proves freeUnits < U —
  // `cap` is floor(freeUnits / U), so 0 means "not even one fits", not "none
  // left". That is conclusive for any ticket costing AT LEAST U, and says
  // nothing about a cheaper one: a 2-unit tandem reading 0 leaves room for a
  // 1-unit adult, but none for a 4-unit quad.
  //
  // Requiring U === 1 (an earlier attempt at this) was sound but threw the
  // multi-unit signal away, leaving a quad selectable on a pool that provably
  // could not seat it — refusing on selection with no row showing a limit,
  // which is the dead end this function exists to prevent.
  //
  // `freeUnits` would settle every case exactly; the service computes it per
  // window but the multi-ticket path stamps only per-ticket maxTickets on the
  // slot, so it is not available here. Carded.
  const ownUnits = Math.max(1, ticket.resourceUnitsPerTicket ?? 1);
  for (const sibling of ticketTypes) {
    if (sibling.resourcePoolId !== ticket.resourcePoolId) continue;
    const cap = capsByTicketTypeId[sibling.id];
    if (typeof cap !== "number" || !Number.isFinite(cap)) continue;
    const units = Math.max(1, sibling.resourceUnitsPerTicket ?? 1);
    if (units <= ownUnits && Math.max(0, cap) === 0) return true;
  }
  return false;
}
